/**
 * Private Assignment of Member Interest
 * Business Entity specific document
 * Requires owner signature
 */

import { replaceMergeFields, getAllMergeFields } from './mergeFields';

export function generatePrivateAssignmentHtml(
  workflowData: any,
  unaData: any
): string {
  const mergeFields = getAllMergeFields(workflowData, unaData);
  
  const template = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body {
      font-family: 'Cambria', 'Georgia', serif;
      font-size: 12pt;
      line-height: 1.8;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 1in;
    }
    h1 {
      text-align: center;
      font-size: 16pt;
      margin-bottom: 0.5in;
      font-weight: bold;
      text-transform: uppercase;
    }
    .recital {
      margin-bottom: 0.25in;
      text-align: justify;
    }
    .section {
      margin-bottom: 0.3in;
      text-align: justify;
    }
    .section-title {
      font-weight: bold;
      margin-bottom: 0.1in;
    }
    .signature-section {
      margin-top: 0.75in;
      page-break-inside: avoid;
    }
    .signature-block {
      margin-top: 0.5in;
    }
    .signature-line {
      border-bottom: 1px solid #000;
      width: 3.5in;
      margin-top: 0.4in;
      margin-bottom: 0.1in;
    }
    .signature-label {
      font-size: 10pt;
    }
    .date-line {
      border-bottom: 1px solid #000;
      width: 2in;
      display: inline-block;
      margin-bottom: 0.1in;
    }
    .notary-box {
      border: 2px solid #000;
      padding: 0.3in;
      margin-top: 0.5in;
    }
    .notary-title {
      font-weight: bold;
      text-align: center;
      margin-bottom: 0.2in;
    }
  </style>
</head>
<body>
  <h1>Private Assignment of Member Interest</h1>
  
  <div class="recital">
    This Private Assignment of Member Interest (this "Assignment") is entered into as of 
    {{contribution_date}}, by and between:
  </div>
  
  <div class="recital" style="margin-left: 0.5in;">
    <strong>ASSIGNOR:</strong> {{contributor_name}}, residing at {{contributor_address}} 
    ("Assignor"), a member of {{una_name}};
  </div>
  
  <div class="recital" style="margin-left: 0.5in;">
    <strong>ASSIGNEE:</strong> {{receiving_entity}} ("Assignee"), an entity under the 
    stewardship of {{una_name}}, an unincorporated nonprofit association formed under 
    the laws of {{una_state}}.
  </div>
  
  <div class="recital">
    <strong>RECITALS:</strong>
  </div>
  
  <div class="recital">
    A. Assignor is the holder of a {{ownership_percentage}}% member interest (the "Interest") 
    in {{entity_legal_name}}, a {{entity_type}} formed under the laws of {{entity_state_of_formation}} 
    (the "Company");
  </div>
  
  <div class="recital">
    B. Assignor desires to make a private member contribution of the Interest to Assignee 
    within the ecclesiastical jurisdiction of {{una_name}}, and Assignee desires to accept 
    such contribution and assume stewardship of the Interest;
  </div>
  
  <div class="recital">
    C. This Assignment is made as a private ecclesiastical transaction and is not subject 
    to public law gift or transfer regulations.
  </div>
  
  <div class="recital">
    <strong>NOW, THEREFORE</strong>, in consideration of the mutual covenants and agreements 
    contained herein, and for other good and valuable consideration within the ecclesiastical 
    jurisdiction, the receipt and sufficiency of which are hereby acknowledged, the parties 
    agree as follows:
  </div>
  
  <div class="section">
    <div class="section-title">1. ASSIGNMENT OF INTEREST</div>
    Assignor hereby assigns, transfers, and conveys to Assignee all of Assignor's right, 
    title, and interest in and to the Interest, including all rights to profits, losses, 
    distributions, and voting rights associated with such Interest, effective as of 
    {{contribution_date}}.
  </div>
  
  <div class="section">
    <div class="section-title">2. ECCLESIASTICAL NATURE</div>
    The parties acknowledge and agree that this Assignment is made within the private 
    ecclesiastical jurisdiction of {{una_name}}, and is not a public transfer, gift, 
    donation, or sale under state or federal law. This transaction is governed by the 
    principles and customs of ecclesiastical law.
  </div>
  
  <div class="section">
    <div class="section-title">3. REPRESENTATIONS AND WARRANTIES</div>
    Assignor represents and warrants that: (a) Assignor is the lawful holder of the Interest 
    and has full right and authority to assign the Interest; (b) the Interest is free and 
    clear of all liens, encumbrances, and claims; (c) no consent or approval of any third 
    party is required for this Assignment, or if required, such consent has been obtained.
  </div>
  
  <div class="section">
    <div class="section-title">4. STEWARDSHIP</div>
    Assignee accepts stewardship of the Interest and agrees to manage and operate the 
    Company in accordance with the purposes and principles of {{una_name}}, for the 
    benefit of the membership.
  </div>
  
  <div class="section">
    <div class="section-title">5. BINDING EFFECT</div>
    This Assignment shall be binding upon and inure to the benefit of the parties and 
    their respective successors and assigns within the ecclesiastical jurisdiction.
  </div>
  
  <div class="section">
    <div class="section-title">6. GOVERNING LAW</div>
    This Assignment is made under ecclesiastical jurisdiction and the customs of {{una_name}}.
  </div>
  
  <div class="signature-section">
    <div style="text-align: center; font-weight: bold; margin-bottom: 0.3in;">
      IN WITNESS WHEREOF, the parties have executed this Assignment as of the date first 
      written above.
    </div>
    
    <div class="signature-block">
      <div style="font-weight: bold; margin-bottom: 0.2in;">ASSIGNOR:</div>
      <div class="signature-line"></div>
      <div class="signature-label">{{contributor_name}}</div>
      <div style="margin-top: 0.3in;">
        Date: <div class="date-line"></div>
      </div>
    </div>
    
    <div class="signature-block">
      <div style="font-weight: bold; margin-bottom: 0.2in;">ASSIGNEE:</div>
      <div style="margin-bottom: 0.1in;">{{receiving_entity}}</div>
      <div class="signature-line" data-signer-id="{{treasury_secretary_id}}" data-signature-role="primary_officer"></div>
      <div class="signature-label">By: {{treasury_secretary_name}}, {{primary_officer_role}}</div>
      <div class="signature-label" style="margin-left: 0.5in;">On behalf of {{una_name}}</div>
      <div style="margin-top: 0.3in;">
        Date: <div class="date-line"></div>
      </div>
    </div>
  </div>
  
  <div class="notary-box">
    <div class="notary-title">ACKNOWLEDGMENT</div>
    <div style="font-size: 10pt; line-height: 2;">
      State of: _________________________<br>
      County of: ________________________<br><br>
      
      On __________________, before me, ______________________, a Notary Public, 
      personally appeared {{contributor_name}}, who proved to me on the basis of 
      satisfactory evidence to be the person whose name is subscribed to the within 
      instrument and acknowledged to me that he/she executed the same.<br><br>
      
      <div style="margin-top: 0.3in;">
        <div style="border-bottom: 1px solid #000; width: 3in; display: inline-block;"></div><br>
        <div style="font-size: 9pt;">Notary Public Signature</div>
      </div>
    </div>
  </div>
  
  <div style="margin-top: 0.3in; font-size: 10pt; color: #666; font-style: italic;">
    Note: Notarization is recommended but not required for completion of this workflow.
  </div>
</body>
</html>
  `;
  
  return replaceMergeFields(template, mergeFields);
}
